import { ConnectionQuality, ConnectionState } from 'livekit-client'

import type {
  ConnectionObservation,
  ConnectionTierChange,
  ConnectionTierTracker,
  ReceptionAudioStats,
  ReceptionSample,
  ReceptionTracker,
  ReceptionVerdict,
  ReceptionVideoStats,
} from '@ts-classroom/interfaces'
import type { ConnectionSide } from '@ts-classroom/types'

import { ConnectionTier } from '@ts-classroom/enums'

import {
  CONNECTION_BLAME_SUSTAIN_MS,
  CONNECTION_CPU_LIMITATION_HOLD_MS,
  CONNECTION_PEER_LOST_MS,
  CONNECTION_RATING_SUSTAIN_MS,
  CONNECTION_RECONNECTING_MS,
  CONNECTION_RECOVERY_SUSTAIN_MS,
  CONNECTION_START_GRACE_MS,
  RECEPTION_CONCEALMENT_RATIO_MAX,
  RECEPTION_FROZEN_SECONDS_MIN,
  RECEPTION_JUDGED_SAMPLES_MIN,
  RECEPTION_STALL_MS,
  RECEPTION_WINDOW_MS,
} from '@config/connection-quality'

type ReceptionKind = 'video' | 'audio'

interface RatingStreaks {
  belowExcellent: number | null
  poor: number | null
  excellent: number | null
  lost: number | null
}

interface SideVerdictInput {
  current: ConnectionTier
  isLost: boolean
  canBeBlamed: boolean
  streaks: RatingStreaks
}

interface SideLink {
  rank: number
  isUplinkImpaired: boolean
  isReceptionDegraded: boolean
}

const RECEPTION_KINDS: ReceptionKind[] = ['video', 'audio']
const SIDES: ConnectionSide[] = ['self', 'peer']

const QUALITY_RANK: Record<ConnectionQuality, number> = {
  [ConnectionQuality.Excellent]: 3,
  [ConnectionQuality.Unknown]: 3,
  [ConnectionQuality.Good]: 2,
  [ConnectionQuality.Poor]: 1,
  [ConnectionQuality.Lost]: 0,
}

const isBelowExcellentRating = (quality: ConnectionQuality): boolean =>
  quality === ConnectionQuality.Good || quality === ConnectionQuality.Poor

const isExcellentOrUnratedRating = (quality: ConnectionQuality): boolean =>
  quality === ConnectionQuality.Excellent || quality === ConnectionQuality.Unknown

const coveredWindow = <T>(window: ReceptionSample[], pick: (sample: ReceptionSample) => T | null): T[] | null => {
  const picked = window.map(pick)
  const isEveryMediaSample = (values: (T | null)[]): values is T[] => values.every((value) => value !== null)

  if (picked.length < RECEPTION_JUDGED_SAMPLES_MIN || !isEveryMediaSample(picked)) return null

  return picked
}

const frozenSecondsOf = (videos: ReceptionVideoStats[]): number => {
  const first = videos[0]
  const last = videos[videos.length - 1]
  if (!first || !last) return 0

  const hasFreezeCounters = first.totalFreezesDuration !== null && last.totalFreezesDuration !== null
  const recordedFreezeSeconds = hasFreezeCounters
    ? (last.totalFreezesDuration ?? 0) - (first.totalFreezesDuration ?? 0)
    : 0
  const stillPictureSeconds = videos.filter((video) => video.isPaused || video.framesPerSecond === 0).length

  return Math.max(recordedFreezeSeconds, stillPictureSeconds)
}

const concealmentRatioOf = (audios: ReceptionAudioStats[]): number => {
  const first = audios[0]
  const last = audios[audios.length - 1]
  if (!first || !last) return 0

  const receivedSamples = last.totalSamplesReceived - first.totalSamplesReceived
  const audibleConcealedSamples = (last.concealedSamples - first.concealedSamples)
    - (last.silentConcealedSamples - first.silentConcealedSamples)

  return receivedSamples > 0 ? audibleConcealedSamples / receivedSamples : 0
}

const bytesOf = (sample: ReceptionSample, kind: ReceptionKind): number => sample[kind]?.bytesReceived ?? 0

const isMediaStalled = (window: ReceptionSample[], current: ReceptionSample): boolean => {
  const reference = [...window].reverse().find((sample) => sample.at <= current.at - RECEPTION_STALL_MS)
  if (!reference) return false

  const sinceReference = window.filter((sample) => sample.at >= reference.at)
  const expectedKinds = RECEPTION_KINDS.filter((kind) => sinceReference.every((sample) => sample[kind] !== null))

  return expectedKinds.length > 0
    && expectedKinds.every((kind) => bytesOf(current, kind) <= bytesOf(reference, kind))
}

export const createReceptionTracker = (): ReceptionTracker => {
  let samples: ReceptionSample[] = []

  const push = (sample: ReceptionSample): ReceptionVerdict => {
    samples = [...samples, sample].filter((kept) => sample.at - kept.at <= RECEPTION_WINDOW_MS)

    const videos = coveredWindow(samples, (kept) => kept.video)
    const audios = coveredWindow(samples, (kept) => kept.audio)
    const frozenSeconds = videos ? frozenSecondsOf(videos) : null
    const concealmentRatio = audios ? concealmentRatioOf(audios) : null
    const isFrozen = frozenSeconds !== null && frozenSeconds >= RECEPTION_FROZEN_SECONDS_MIN
    const isGarbled = concealmentRatio !== null && concealmentRatio >= RECEPTION_CONCEALMENT_RATIO_MAX

    return {
      isDegraded: isFrozen || isGarbled,
      isStalled: isMediaStalled(samples, sample),
      frozenSeconds,
      concealmentRatio,
    }
  }

  const reset = (): void => {
    samples = []
  }

  return { push, reset }
}

const noStreaks = (): RatingStreaks => ({
  belowExcellent: null,
  poor: null,
  excellent: null,
  lost: null,
})

const streakSince = (since: number | null, holds: boolean, at: number): number | null => (holds ? since ?? at : null)

const nextStreaks = (previous: RatingStreaks, quality: ConnectionQuality, at: number): RatingStreaks => ({
  belowExcellent: streakSince(previous.belowExcellent, isBelowExcellentRating(quality), at),
  poor: streakSince(previous.poor, quality === ConnectionQuality.Poor, at),
  excellent: streakSince(previous.excellent, isExcellentOrUnratedRating(quality), at),
  lost: streakSince(previous.lost, quality === ConnectionQuality.Lost, at),
})

const isBlamedOver = (side: SideLink, other: SideLink): boolean => {
  if (side.rank !== other.rank) return side.rank < other.rank
  if (side.isUplinkImpaired || other.isUplinkImpaired) return side.isUplinkImpaired

  return side.isReceptionDegraded
}

export const createConnectionTierTracker = (): ConnectionTierTracker => {
  const tiers: Record<ConnectionSide, ConnectionTier> = { self: ConnectionTier.Good, peer: ConnectionTier.Good }
  const streaks: Record<ConnectionSide, RatingStreaks> = { self: noStreaks(), peer: noStreaks() }
  const blameSince: Record<ConnectionSide, number | null> = { self: null, peer: null }
  let reconnectingSince: number | null = null
  let cleanReceptionSince: number | null = null
  let graceStartedAt: number | null = null
  let peerPresentSince: number | null = null
  let cpuLimitedAt: number | null = null

  const push = (observation: ConnectionObservation): ConnectionTierChange[] => {
    const { at, peerQuality } = observation
    const isReconnecting = observation.roomState === ConnectionState.Reconnecting
    const isConnected = observation.roomState === ConnectionState.Connected
    const lasted = (since: number | null, durationMs: number): boolean => since !== null && at - since >= durationMs

    cpuLimitedAt = observation.isCpuLimited ? at : cpuLimitedAt

    const isCpuLimitationRecent = cpuLimitedAt !== null && at - cpuLimitedAt < CONNECTION_CPU_LIMITATION_HOLD_MS
    const isReceptionDegraded = !isCpuLimitationRecent
      && (observation.isOwnReceptionDegraded || observation.isPeerReceptionDegraded)

    reconnectingSince = streakSince(reconnectingSince, isReconnecting, at)
    cleanReceptionSince = streakSince(cleanReceptionSince, !isReceptionDegraded, at)
    graceStartedAt = isReconnecting ? null : graceStartedAt ?? at
    peerPresentSince = streakSince(peerPresentSince, peerQuality !== null, at)
    streaks.self = nextStreaks(streaks.self, observation.selfQuality, at)
    streaks.peer = peerQuality === null ? noStreaks() : nextStreaks(streaks.peer, peerQuality, at)

    const isPastStartGrace = lasted(graceStartedAt, CONNECTION_START_GRACE_MS)
      && lasted(peerPresentSince, CONNECTION_START_GRACE_MS)
    const isReceptionClean = lasted(cleanReceptionSince, CONNECTION_RECOVERY_SUSTAIN_MS)
    const selfLink: SideLink = {
      rank: QUALITY_RANK[observation.selfQuality],
      isUplinkImpaired: observation.isOwnUplinkImpaired,
      isReceptionDegraded: observation.isOwnReceptionDegraded,
    }
    const peerLink: SideLink = {
      rank: peerQuality === null ? QUALITY_RANK[ConnectionQuality.Excellent] : QUALITY_RANK[peerQuality],
      isUplinkImpaired: observation.isPeerUplinkImpaired,
      isReceptionDegraded: observation.isPeerReceptionDegraded,
    }

    blameSince.self = streakSince(
      blameSince.self,
      !isReconnecting && peerQuality !== ConnectionQuality.Lost && isBlamedOver(selfLink, peerLink),
      at,
    )
    blameSince.peer = streakSince(
      blameSince.peer,
      isConnected && peerQuality !== null && isBlamedOver(peerLink, selfLink),
      at,
    )

    const nextTier =({ current, isLost, canBeBlamed, streaks: rating }: SideVerdictInput): ConnectionTier => {
      if (isLost) return ConnectionTier.Lost
      if (current === ConnectionTier.Lost) return ConnectionTier.Good

      const isRatedPoor = lasted(rating.poor, CONNECTION_RATING_SUSTAIN_MS)
      const isDegradationConfirmed = lasted(rating.belowExcellent, CONNECTION_RATING_SUSTAIN_MS)
        && isReceptionDegraded
        && canBeBlamed

      if (current === ConnectionTier.Good) {
        if (!isDegradationConfirmed || !isPastStartGrace) return ConnectionTier.Good

        return isRatedPoor ? ConnectionTier.Weak : ConnectionTier.Unstable
      }

      const isRecovered = isReceptionClean || lasted(rating.excellent, CONNECTION_RECOVERY_SUSTAIN_MS)
      if (isRecovered) return ConnectionTier.Good

      const isEscalatingToWeak = isDegradationConfirmed && isRatedPoor

      return isEscalatingToWeak ? ConnectionTier.Weak : current
    }

    const next: Record<ConnectionSide, ConnectionTier> = {
      self: nextTier({
        current: tiers.self,
        isLost: lasted(reconnectingSince, CONNECTION_RECONNECTING_MS),
        canBeBlamed: lasted(blameSince.self, CONNECTION_BLAME_SUSTAIN_MS),
        streaks: streaks.self,
      }),
      peer: peerQuality === null ? ConnectionTier.Good : nextTier({
        current: tiers.peer,
        isLost: isConnected && lasted(streaks.peer.lost, CONNECTION_PEER_LOST_MS) && observation.isPeerMediaStalled,
        canBeBlamed: lasted(blameSince.peer, CONNECTION_BLAME_SUSTAIN_MS),
        streaks: streaks.peer,
      }),
    }

    return SIDES
      .filter((side) => next[side] !== tiers[side])
      .map((side) => {
        const change: ConnectionTierChange = { side, tier: next[side], previousTier: tiers[side] }
        tiers[side] = next[side]

        return change
      })
  }

  const current = (side: ConnectionSide): ConnectionTier => tiers[side]

  const resetSide = (side: ConnectionSide): void => {
    tiers[side] = ConnectionTier.Good
    streaks[side] = noStreaks()
    blameSince[side] = null

    if (side === 'peer') {
      peerPresentSince = null

      return
    }

    reconnectingSince = null
    graceStartedAt = null
  }

  return { push, current, resetSide }
}
